// ✅ AdminLogs.jsx — Live snapshot, debounced search, tolerant dates, CSV export, optional paging, layout preserved
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  query as fsQuery,
  orderBy,
  limit as fsLimit,
  startAfter as fsStartAfter,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';

// ---------- Helpers ----------
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const coerceDate = (d) => {
  try {
    if (!d) return null;
    if (d?.seconds) return new Date(d.seconds * 1000);
    if (typeof d === 'number') return new Date(d);
    if (typeof d === 'string') {
      const parsed = new Date(d);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (d instanceof Date) return d;
    return null;
  } catch {
    return null;
  }
};
const fmtDateTime = (d) => (d ? d.toLocaleString() : '—');

const escapeCSV = (val) => {
  const s = String(val ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// ---------- Component ----------
export default function AdminLogs() {
  const navigate = useNavigate();

  // Live pages (latest N)
  const [liveCamel, setLiveCamel] = useState([]);     // adminLogs
  const [liveSnake, setLiveSnake] = useState([]);     // admin_logs
  const lastCamelRef = useRef(null);
  const lastSnakeRef = useRef(null);

  // Paged older results (optional)
  const [olderCamel, setOlderCamel] = useState([]);
  const [olderSnake, setOlderSnake] = useState([]);
  const [hasMoreCamel, setHasMoreCamel] = useState(true);
  const [hasMoreSnake, setHasMoreSnake] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);

  // UI
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const debouncedFilter = useDebounce(filter, 250);

  const mountedRef = useRef(true);
  const PAGE_SIZE = 100;

  useEffect(() => {
    mountedRef.current = true;

    const sub = (collName, setter, lastRefStore) => {
      const q = fsQuery(
        collection(db, collName),
        orderBy('timestamp', 'desc'),
        fsLimit(PAGE_SIZE)
      );
      return onSnapshot(
        q,
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            __src: collName,
            ...doc.data(),
          }));
          setter(data);
          lastRefStore.current = snapshot.docs[snapshot.docs.length - 1] || null;
          setLoading(false);
        },
        (err) => {
          console.error(`❌ Error fetching ${collName}:`, err);
          setLoading(false);
        }
      );
    };

    const unsubCamel = sub('adminLogs', setLiveCamel, lastCamelRef);
    const unsubSnake = sub('admin_logs', setLiveSnake, lastSnakeRef);

    return () => {
      mountedRef.current = false;
      unsubCamel();
      unsubSnake();
    };
  }, []);

  // Choose primary collection for paging (prefer camel if present)
  const activeCollection = useMemo(() => {
    if (liveCamel.length > 0) return 'adminLogs';
    if (liveSnake.length > 0) return 'admin_logs';
    return 'adminLogs';
  }, [liveCamel.length, liveSnake.length]);

  const combined = useMemo(() => {
    // Merge keeping newest first; IDs may collide across collections, so namespace by id+src
    const all = [...liveCamel, ...olderCamel, ...liveSnake, ...olderSnake];
    const byKey = new Map();
    for (const item of all) {
      const key = `${item.__src || 'adminLogs'}::${item.id}`;
      if (!byKey.has(key)) byKey.set(key, item);
    }
    const arr = Array.from(byKey.values());
    arr.sort((a, b) => {
      const ta = coerceDate(a.timestamp)?.getTime() ?? 0;
      const tb = coerceDate(b.timestamp)?.getTime() ?? 0;
      return tb - ta;
    });
    return arr;
  }, [liveCamel, liveSnake, olderCamel, olderSnake]);

  const filteredLogs = useMemo(() => {
    const q = debouncedFilter.trim().toLowerCase();
    if (!q) return combined;
    return combined.filter((log) => {
      const hay = [
        log.action,
        log.adminId,
        log.targetUserId,
        log.uid,
        log.subscriptionTier,
        log.chargeId,
        log.lastChargeId,
        JSON.stringify(log.details || {}),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [combined, debouncedFilter]);

  const exportCSV = useCallback(() => {
    const headers = [
      'Timestamp',
      'Admin',
      'Action',
      'TargetUser',
      'Tier',
      'BillingDate',
      'ChargeID',
      'DetailsJSON',
      'Source',
      'DocID',
    ];
    const rows = filteredLogs.map((log) => [
      fmtDateTime(coerceDate(log.timestamp)),
      log.adminId || '',
      log.action || log.type || '',
      log.targetUserId || log.uid || '',
      log.subscriptionTier || '',
      fmtDateTime(coerceDate(log.nextBillingDate)),
      log.chargeId || log.lastChargeId || '',
      JSON.stringify(log.details || {}),
      log.__src || 'adminLogs',
      log.id,
    ]);
    const csv = [headers, ...rows].map((r) => r.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'admin_logs.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredLogs]);

  const loadMore = useCallback(async () => {
    // Page older results from whichever collection is active
    if (pageLoading) return;
    setPageLoading(true);
    try {
      const useCamel = activeCollection === 'adminLogs';
      const lastRef = useCamel ? lastCamelRef.current : lastSnakeRef.current;
      const hasMore = useCamel ? hasMoreCamel : hasMoreSnake;
      if (!hasMore || !lastRef) {
        setPageLoading(false);
        return;
      }
      const q = fsQuery(
        collection(db, useCamel ? 'adminLogs' : 'admin_logs'),
        orderBy('timestamp', 'desc'),
        fsStartAfter(lastRef),
        fsLimit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const page = snap.docs.map((d) => ({
        id: d.id,
        __src: useCamel ? 'adminLogs' : 'admin_logs',
        ...d.data(),
      }));
      if (useCamel) {
        setOlderCamel((prev) => [...prev, ...page]);
        lastCamelRef.current = snap.docs[snap.docs.length - 1] || null;
        setHasMoreCamel(page.length === PAGE_SIZE);
      } else {
        setOlderSnake((prev) => [...prev, ...page]);
        lastSnakeRef.current = snap.docs[snap.docs.length - 1] || null;
        setHasMoreSnake(page.length === PAGE_SIZE);
      }
    } catch (e) {
      console.error('❌ Load more failed:', e);
    } finally {
      setPageLoading(false);
    }
  }, [activeCollection, pageLoading, hasMoreCamel, hasMoreSnake]);

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.header}>🛡️ Admin Activity Logs ({combined.length})</h2>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search action, user ID, or admin ID..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={styles.search}
          aria-label="Search logs"
        />
        <button onClick={exportCSV} style={styles.exportBtn}>Export CSV</button>
      </div>

      {loading ? (
        <p>Loading logs...</p>
      ) : (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Timestamp</th>
                <th style={styles.th}>Admin</th>
                <th style={styles.th}>Action</th>
                <th style={styles.th}>Target User</th>
                <th style={styles.th}>Tier</th>
                <th style={styles.th}>Billing Date</th>
                <th style={styles.th}>Charge ID</th>
                <th style={styles.th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={`${log.__src || 'adminLogs'}::${log.id}`}>
                  <td style={styles.td}>
                    {fmtDateTime(coerceDate(log.timestamp))}
                  </td>
                  <td style={styles.td}>{log.adminId || '—'}</td>
                  <td style={styles.td}>{log.action || log.type || '—'}</td>
                  <td style={styles.td}>{log.targetUserId || log.uid || '—'}</td>
                  <td style={styles.td}>{log.subscriptionTier || '—'}</td>
                  <td style={styles.td}>
                    {fmtDateTime(coerceDate(log.nextBillingDate))}
                  </td>
                  <td style={styles.td}>{log.chargeId || log.lastChargeId || '—'}</td>
                  <td style={styles.td}>
                    <pre style={styles.pre}>
                      {JSON.stringify(log.details || {}, null, 2)}
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
            {(activeCollection === 'adminLogs' ? hasMoreCamel : hasMoreSnake) ? (
              <button onClick={loadMore} style={styles.moreBtn} disabled={pageLoading}>
                {pageLoading ? 'Loading…' : 'Load more'}
              </button>
            ) : (
              <span style={{ color: '#6b7280', fontSize: 12 }}>End of results</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  backButton: {
    marginBottom: '1rem',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#eee',
    cursor: 'pointer',
  },
  header: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#0f5132',
  },
  search: {
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    width: '100%',
    maxWidth: '300px',
  },
  exportBtn: {
    backgroundColor: '#065f46',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 0.85rem',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    boxShadow: '0 0 10px rgba(0,0,0,0.05)',
  },
  th: {
    borderBottom: '2px solid #ccc',
    padding: '0.75rem',
    textAlign: 'left',
    backgroundColor: '#e6f2ff',
  },
  td: {
    borderBottom: '1px solid #ddd',
    padding: '0.75rem',
    verticalAlign: 'top',
  },
  pre: {
    whiteSpace: 'pre-wrap',
    fontSize: '0.8rem',
    margin: 0,
  },
  moreBtn: {
    background: '#e5e7eb',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
};
